// PROF-1 — la PRIMA riga `PROFILE` di chi è appena entrato su invito.
//
// ⛔ Vive qui, e non nei due montaggi, perché è **una decisione di prodotto in
// tre righe** e le decisioni non si scrivono due volte: quale campo è
// obbligatorio, che la scrittura è diretta e non get-merge, e cosa succede se
// fallisce. Scritta nei due `WelcomeGate` sarebbe stata la solita coppia che
// diverge appena uno dei due cambia idea.
//
// ⛔ **PUT diretta, mai gli sportelli get-merge-PUT** (`useProfileDoc.savePatch`
// / `patchProfile`): rileggono il documento prima di fondere, e qui il
// documento **non esiste ancora** — misurato l'1/09, la GET risponde 404. La
// PUT lato API è create-or-update, quindi la riga nasce da questa sola chiamata.
//
// ⚠️ `name` è l'unico campo dello schema senza default (`schemas/profile.ts`):
// `unitsPreference` e gli altri li riempie zod. Per questo il form chiede il
// nome e nient'altro.

/** Il minimo che serve: la firma che entrambi i mount già espongono.
 *  ⛔ `method` è il letterale "PUT", non `string`: i due `apiFetch` tipizzano
 *  il verbo come unione chiusa, e allargarlo qui li renderebbe non assegnabili.
 *  È anche più onesto — questa funzione fa una cosa sola. */
type ProfileWriter = (
  path: string,
  opts: { method: "PUT" | "POST"; body: unknown },
) => Promise<unknown>;

/**
 * Crea la riga profilo. ⛔ NON rilancia: un fallimento qui non deve rimandare
 * indietro chi è appena entrato — la sfida Cognito è già chiusa e la password
 * temporanea non apre più niente, quindi «torna alla porta» manderebbe l'atleta
 * a una porta murata. Restituisce l'esito così che chi chiama possa dirlo,
 * invece di scoprirlo da un `console.warn` che non legge nessuno.
 */
/** What did not happen, when something did not: the row itself, or (row
 *  written) the age declaration, which the gate asks again at the next entry. */
export type InitialProfileResult =
  | { ok: true }
  | { ok: false; failed: "profile" | "ageDeclaration"; error: unknown };

export const createInitialProfile = async (
  apiFetch: ProfileWriter,
  name: string,
  /** #175: when the athlete ticked the age declaration. Posted to its own
   *  route right after the row exists. */
  ageDeclaredAt?: string,
): Promise<InitialProfileResult> => {
  try {
    await apiFetch("/api/profiles", { method: "PUT", body: { name } });
  } catch (error) {
    return { ok: false, failed: "profile", error };
  }
  if (ageDeclaredAt === undefined) return { ok: true };
  try {
    await apiFetch("/api/profiles/age-declaration", {
      method: "POST",
      body: { at: ageDeclaredAt },
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, failed: "ageDeclaration", error };
  }
};

/** The two WelcomeGates' log line for a result that did not fully land. */
export const describeInitialProfileFailure = (
  result: Extract<InitialProfileResult, { ok: false }>,
): string =>
  result.failed === "profile"
    ? "[auth] first profile write failed"
    : "[auth] profile written, age declaration not saved";
